import EventBus from "./eventBus";

const PIP_SIZE_RATIO = 0.28;
const PIP_MARGIN_RATIO = 0.035;
const RECT_ASPECT_RATIO = 16 / 9;
const ALLOWED_SHAPES = new Set(["square", "rectangle"]);
const ALLOWED_POSITIONS = new Set([
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
]);

const DEFAULT_PIP_CONFIG = {
  position: "bottom-right",
  shape: "square",
  roundedCorners: true,
};

function drawVideoContain(ctx, video, x, y, width, height) {
  if (!video || video.readyState < 2) {
    return;
  }

  const videoWidth = video.videoWidth || video.width || 1280;
  const videoHeight = video.videoHeight || video.height || 720;

  if (!videoWidth || !videoHeight) {
    try {
      ctx.drawImage(video, x, y, width, height);
    } catch (e) {
      /* ignore draw error */
    }
    return;
  }

  const videoRatio = videoWidth / videoHeight;
  const targetRatio = width / height;
  let renderW = width;
  let renderH = height;
  let renderX = x;
  let renderY = y;

  if (Math.abs(videoRatio - targetRatio) > 0.01) {
    if (videoRatio > targetRatio) {
      renderW = width;
      renderH = width / videoRatio;
      renderY = y + (height - renderH) / 2;
    } else {
      renderH = height;
      renderW = height * videoRatio;
      renderX = x + (width - renderW) / 2;
    }
  }

  try {
    ctx.drawImage(
      video,
      0,
      0,
      videoWidth,
      videoHeight,
      renderX,
      renderY,
      renderW,
      renderH
    );
  } catch (e) {
    /* ignore draw error */
  }
}

function drawVideoCover(ctx, video, x, y, width, height) {
  if (!video || video.readyState < 2) {
    return;
  }

  const videoWidth = video.videoWidth || video.width || 1280;
  const videoHeight = video.videoHeight || video.height || 720;

  if (!videoWidth || !videoHeight) {
    return;
  }

  const videoRatio = videoWidth / videoHeight;
  const targetRatio = width / height;
  let sx = 0;
  let sy = 0;
  let sWidth = videoWidth;
  let sHeight = videoHeight;

  if (Math.abs(videoRatio - targetRatio) > 0.01) {
    if (videoRatio > targetRatio) {
      sHeight = videoHeight;
      sWidth = sHeight * targetRatio;
      sx = (videoWidth - sWidth) / 2;
    } else {
      sWidth = videoWidth;
      sHeight = sWidth / targetRatio;
      sy = (videoHeight - sHeight) / 2;
    }
  }

  ctx.drawImage(video, sx, sy, sWidth, sHeight, x, y, width, height);
}

function drawRoundedRect(ctx, x, y, width, height, radius) {
  const clampedRadius = Math.min(radius, width / 2, height / 2);
  ctx.moveTo(x + clampedRadius, y);
  ctx.lineTo(x + width - clampedRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + clampedRadius);
  ctx.lineTo(x + width, y + height - clampedRadius);
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - clampedRadius,
    y + height
  );
  ctx.lineTo(x + clampedRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - clampedRadius);
  ctx.lineTo(x, y + clampedRadius);
  ctx.quadraticCurveTo(x, y, x + clampedRadius, y);
}

function computePipRect(canvas, shape, position) {
  const margin = Math.min(canvas.width, canvas.height) * PIP_MARGIN_RATIO;
  let pipWidth = canvas.width * PIP_SIZE_RATIO;
  let pipHeight = pipWidth;

  if (shape === "rectangle") {
    pipHeight = pipWidth / RECT_ASPECT_RATIO;
  } else if (shape === "square") {
    const maxDimension = Math.min(pipWidth, canvas.height * PIP_SIZE_RATIO);
    pipWidth = maxDimension;
    pipHeight = maxDimension;
  }

  const maxHeight = canvas.height * 0.42;
  if (pipHeight > maxHeight) {
    const scale = maxHeight / pipHeight;
    pipHeight = maxHeight;
    pipWidth *= scale;
  }

  let x = margin;
  let y = margin;

  switch (position) {
    case "top-right":
      x = canvas.width - pipWidth - margin;
      y = margin;
      break;
    case "bottom-left":
      x = margin;
      y = canvas.height - pipHeight - margin;
      break;
    case "bottom-right":
      x = canvas.width - pipWidth - margin;
      y = canvas.height - pipHeight - margin;
      break;
    default:
      // top-left is the default fallback
      x = margin;
      y = margin;
  }

  return { x, y, width: pipWidth, height: pipHeight };
}

export default class LayoutManager {
  constructor({ bus = new EventBus() } = {}) {
    this.bus = bus;
    this.layouts = {
      pip: {
        id: "pip",
        label: "Picture-in-Picture",
        description:
          "Schermo principale con webcam in miniatura personalizzabile",
        requires: { screen: true, cameras: 1 },
      },
    };
    this.activeLayout = "pip";
    this.activeCameraIndex = 0;
    this.pipConfig = { ...DEFAULT_PIP_CONFIG };
  }

  getLayouts() {
    return Object.values(this.layouts);
  }

  getActiveLayout() {
    return this.layouts[this.activeLayout];
  }

  setLayout(layoutId) {
    if (!this.layouts[layoutId] || this.activeLayout === layoutId) return;
    this.activeLayout = layoutId;
    this.bus.emit("layout:changed", layoutId);
  }

  getPipConfig() {
    return { ...this.pipConfig };
  }

  setPipShape(shape) {
    if (!ALLOWED_SHAPES.has(shape) || this.pipConfig.shape === shape) return;
    this.pipConfig.shape = shape;
    this.bus.emit("layout:pip-config", this.getPipConfig());
  }

  setPipPosition(position) {
    if (
      !ALLOWED_POSITIONS.has(position) ||
      this.pipConfig.position === position
    )
      return;
    this.pipConfig.position = position;
    this.bus.emit("layout:pip-config", this.getPipConfig());
  }

  setPipRoundedCorners(enabled) {
    const value = Boolean(enabled);
    if (this.pipConfig.roundedCorners === value) return;
    this.pipConfig.roundedCorners = value;
    this.bus.emit("layout:pip-config", this.getPipConfig());
  }

  setActiveCameraIndex(index) {
    this.activeCameraIndex = index;
    this.bus.emit("layout:camera", index);
  }

  getActiveCameraIndex() {
    return this.activeCameraIndex;
  }

  cycleCamera(cameraCount) {
    if (!cameraCount) return;
    this.activeCameraIndex = (this.activeCameraIndex + 1) % cameraCount;
    this.bus.emit("layout:camera", this.activeCameraIndex);
  }

  getDrawRoutine() {
    return (params) => this.draw(params);
  }

  draw({ ctx, canvas, sources, activeCameraIndex }) {
    if (this.activeLayout === "pip") {
      this.drawPip({ ctx, canvas, sources, activeCameraIndex });
    }
  }

  drawPip({ ctx, canvas, sources, activeCameraIndex }) {
    const { position, shape, roundedCorners } = this.pipConfig;
    const { width, height } = canvas;
    const activeCamera =
      sources.cameras[activeCameraIndex] ?? sources.cameras[0];

    ctx.fillStyle = "#0f172a";
    ctx.fillRect(0, 0, width, height);

    if (sources.screen) {
      drawVideoContain(ctx, sources.screen, 0, 0, width, height);
    }

    if (!activeCamera) return;

    const rect = computePipRect(canvas, shape, position);

    ctx.save();
    ctx.beginPath();
    const baseRadius = roundedCorners
      ? Math.min(rect.width, rect.height) * 0.18
      : 0;
    drawRoundedRect(ctx, rect.x, rect.y, rect.width, rect.height, baseRadius);
    ctx.closePath();
    ctx.clip();

    drawVideoCover(ctx, activeCamera, rect.x, rect.y, rect.width, rect.height);
    ctx.restore();
  }
}
